// apps/admin/src/components/CollapsibleNav.tsx
// Collapsible sidebar navigation with toggle, hover glow, and localStorage persistence

import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { ChevronLeft, ChevronRight, LogOut } from 'lucide-react';

// Storage key for sidebar state persistence
const SIDEBAR_COLLAPSED_KEY = 'valutten-admin-sidebar-collapsed';

interface Theme {
  name: string;
  from: string;
  to: string;
  hover: string;
  active: string;
  glow: string;
  icon: React.ComponentType<{ className?: string }>;
}

interface NavItem {
  href: string;
  theme: string;
}

interface CollapsibleNavProps {
  navigation: NavItem[];
  themes: Record<string, Theme>;
  user: { displayName?: string | null; email?: string | null } | null;
  onSignOut: () => void;
}

export const CollapsibleNav: React.FC<CollapsibleNavProps> = ({
  navigation,
  themes,
  user,
  onSignOut,
}) => {
  const location = useLocation();

  // Initialize collapsed state from localStorage
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
    return saved === 'true';
  });

  // Persist collapse state to localStorage
  useEffect(() => {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(isCollapsed));
  }, [isCollapsed]);

  const toggleCollapse = () => {
    setIsCollapsed(prev => !prev);
  };

  return (
    <nav
      className={`glass-nav flex-shrink-0 flex flex-col h-screen sticky top-0 transition-all duration-200 ease-out ${
        isCollapsed ? 'w-sidebar-collapsed' : 'w-sidebar-expanded'
      }`}
      style={{
        width: isCollapsed ? 'var(--sidebar-collapsed-width)' : 'var(--sidebar-expanded-width)',
      }}
    >
      {/* Logo Area */}
      <div className={`p-6 ${isCollapsed ? 'px-3' : ''}`}>
        {isCollapsed ? (
          <h1 className="text-white text-xl font-bold text-center">V</h1>
        ) : (
          <>
            <h1 className="text-2xl font-bold gradient-text">VALUTTEN Admin</h1>
            <p className="text-sm text-slate-400 mt-1">Founder Portal</p>
          </>
        )}
      </div>

      {/* Toggle Button */}
      <div className={`px-2 mb-2 ${isCollapsed ? 'flex justify-center' : ''}`}>
        <button
          onClick={toggleCollapse}
          className="p-2 rounded-lg bg-slate-700/50 hover:bg-slate-700 text-slate-400 hover:text-white transition-all duration-200"
          title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {isCollapsed ? (
            <ChevronRight className="w-4 h-4" />
          ) : (
            <div className="flex items-center gap-2">
              <ChevronLeft className="w-4 h-4" />
              <span className="text-sm">Collapse</span>
            </div>
          )}
        </button>
      </div>

      {/* Navigation Items */}
      <ul className="flex-1 p-2 space-y-1 overflow-y-auto">
        {navigation.map((item) => {
          const theme = themes[item.theme];
          if (!theme) return null;

          const Icon = theme.icon;
          const isActive = location.pathname.startsWith(item.href);

          return (
            <li key={item.href}>
              <NavLink
                to={item.href}
                className={`
                  flex items-center gap-3 rounded-lg transition-all duration-200
                  theme-nav-hover nav-glow-hover font-medium
                  ${isCollapsed ? 'justify-center px-2 py-2.5' : 'px-4 py-2.5'}
                  ${isActive ? 'text-white nav-glow-active' : 'text-slate-400 hover:text-white'}
                `}
                title={isCollapsed ? theme.name : undefined}
              >
                <div
                  className={`p-1.5 rounded-lg ${
                    isActive ? 'bg-white/10 shadow-lg' : 'bg-transparent'
                  }`}
                  style={{
                    boxShadow: isActive
                      ? `0 0 10px ${theme.from}40`
                      : undefined,
                  }}
                >
                  <Icon
                    className={`w-5 h-5 transition-all ${
                      isActive ? 'gradient-text' : ''
                    }`}
                  />
                </div>
                {!isCollapsed && <span>{theme.name}</span>}
              </NavLink>
            </li>
          );
        })}
      </ul>

      {/* User Area */}
      <div className={`p-4 border-t border-slate-700/50 ${isCollapsed ? 'px-2' : ''}`}>
        {!isCollapsed && (
          <>
            <p className="text-sm font-semibold text-white truncate">
              {user?.displayName}
            </p>
            <p className="text-xs text-slate-400 truncate">{user?.email}</p>
          </>
        )}
        <button
          onClick={onSignOut}
          className={`
            w-full flex items-center gap-2 mt-4 px-4 py-2.5
            bg-red-600/10 text-red-400 rounded-lg
            hover:bg-red-600/20 transition-all duration-200
            ${isCollapsed ? 'justify-center px-2' : 'justify-center'}
          `}
          title={isCollapsed ? 'Sign Out' : undefined}
        >
          <LogOut className="w-4 h-4" />
          {!isCollapsed && <span>Sign Out</span>}
        </button>
      </div>
    </nav>
  );
};

export default CollapsibleNav;
